import Vue from 'vue'
import Vuex from 'vuex'
import storage from 'local-storage-fallback'
import { v4 as uuidv4 } from 'uuid'
import moment from 'moment'
import equals from 'equals'

Vue.use(Vuex)

function getStore(app) {

    function currentSequenceNo() {
        return (new Date()).getTime()
    }

    const getDefaultOrder = function () {
        return {
            data_version: 3,
            started_at: app.formatDateRequest(moment()),
            local_id: uuidv4(),
            outlet_slug: null,
            baskets: {},
            tip: null,
            meta_charge: null,
            form: null,
            previous_sale_id: null,
            checkout_info: null,
            table_cover: 1,
            menu_option: null,
            loading: false,
            checkout_in_progress: false,
        }
    }

    const getNewBasket = function (outlet) {

        const outlet_fields = [
            'id',
            'slug',
            'store_display_name',
            'show_allergy_warnings',
            'phone_no',
            'allergy_message',
            'allergy_link',
        ]

        return {
            sequence_no: currentSequenceNo(),
            outlet: app.objectCopyProperties(outlet, outlet_fields),
            items: [],
            vouchers: [],
            promotions_before_coupon: {
                valid_promotions: [],
                total_discount: 0,
            },
            promotions_after_coupon: {
                valid_promotions: [],
                total_discount: 0,
            },
            coupon: null,
        }
    }

    const getOrCreateOrderBasket = function (state, outlet) {

        if (outlet == null) {
            throw new Error('Outlet undefined')
        }

        if (state.order.baskets.hasOwnProperty(outlet.id) == false) {
            Vue.set(state.order.baskets, outlet.id, getNewBasket(outlet))
        }

        return state.order.baskets[outlet.id]
    }

    const itemIsValid = function (item, loaded_outlet) {
        // Check that product/variant exists
        let found_product = null
        for (let outlet of loaded_outlet.outlets) {
            for (let category of outlet.menu) {
                for (let product of category.items) {
                    if (product.product_id == item.product.product_id) {
                        found_product = product
                    }
                    for (let variant of product.variants) {
                        if (variant.product_id == item.product.product_id) {
                            found_product = variant
                        }
                    }
                }
            }
        }
        if (found_product == null) {
            return false
        }

        for (let item_modifier of item.modifiers) {
            let found_modifier = null
            for (let modifier_set of found_product.modifier_sets) {
                for (let modifier of modifier_set.modifiers) {
                    if (modifier.modifier_id == item_modifier.id) {
                        found_modifier = modifier
                    }
                }
            }
            if (found_modifier == null) {
                return false
            }
        }

        return true
    };

    const TIP_MAX = 500
    const TIP_STEP = 0.5

    const store = new Vuex.Store({
        state: {
            device_id: null,
            ready: false,
            ui: {
                loading: false,
                sidebar_open: false,
                login_redirect: null,
            },
            location: null,
            order: getDefaultOrder(),
            outlet: null,
            user: null,
            token: null,
            token_checked: false,
            seen_hint_modal: false,
            embed: {
                enabled: false,
                outlet_slug: null,
            },
            preset_fulfilment_type: null,
            outlet_refreshed_at: null,
            app_embed: false,
            app_mode: null,
            allow_popup: false,
            embed_client: null,
            order_loaded_directly: false
        },
        getters: {
            ready(state) {
                return state.ready
            },
            orderLoadedDirectly(state) {
                return state.order_loaded_directly
            },
            deviceId(state) {
                return state.device_id
            },
            embedClient(state) {
                return state.embed_client
            },
            sidebarOpen(state) {
                return state.ui.sidebar_open
            },
            isMultiOrder(state) {
                return Object.values(state.order.baskets).length > 1
            },
            allProducts(state) {
                return Object.values(state.order.baskets)
                    .reduce((carry, basket) => {
                        return carry.concat(basket.items)
                    }, [])
            },
            allVouchers(state) {
                return Object.values(state.order.baskets)
                    .reduce((carry, basket) => {
                        return carry.concat(basket.vouchers)
                    }, [])
            },
            itemCount(state, getters) {
                return getters.allProducts.length + getters.allVouchers.length
            },
            hasItems(state, getters) {
                return getters.itemCount > 0
            },
            basketInfo(state, getters) {

                const has_items = getters.hasItems
                const count = getters.itemCount

                const count_quantity_products = getters.allProducts
                    .map(item => item.quantity)
                    .reduce((carry, quantity) => carry + quantity, 0)

                const count_quantity_vouchers = getters.allVouchers
                    .map(item => item.quantity)
                    .reduce((carry, quantity) => carry + quantity, 0)

                const count_quantity = count_quantity_products + count_quantity_vouchers

                const voucher_quantities = getters.allVouchers
                    .reduce((carry, item) => {
                        carry[item.voucher.product_id] = (carry[item.voucher.product_id] || 0) + item.quantity
                        return carry
                    }, {})

                const basket_cost = Object.values(getters.order.baskets)
                    .reduce((carry, basket) => carry.add(basket.total_price), currency(0))
                    .toString()

                const basket_cost_without_coupon = Object.values(getters.order.baskets)
                    .reduce((carry, basket) => carry.add(basket.total_price_without_coupon), currency(0))
                    .toString()

                const basket_cost_without_deductions = Object.values(getters.order.baskets)
                    .reduce((carry, basket) => carry.add(basket.total_price_without_deductions), currency(0))
                    .toString()

                let meta_charge = currency(0)
                if (state.order.meta_charge) {
                    meta_charge = currency(state.order.meta_charge.amount)
                }
                meta_charge = meta_charge.toString()

                const coupon_discount = Object.values(getters.order.baskets)
                    .reduce((carry, basket) => {
                        if (basket.coupon)
                            carry.add(basket.coupon.amount)
                        return carry
                    }, currency(0))
                    .toString()

                const subtotal_with_meta_charge = currency(basket_cost_without_coupon).add(meta_charge).toString()

                const subtotal = currency(basket_cost).add(meta_charge).toString()
                const tip = state.order.tip || 0

                let total = currency(subtotal).add(tip).subtract(coupon_discount)
                if (total.intValue < 0)
                    total = currency(0)
                total = total.toString()

                return {
                    has_items,
                    count,
                    count_quantity,
                    voucher_quantities,
                    amount: {
                        basket_cost,
                        total,
                        subtotal: basket_cost_without_coupon,
                        subtotal_with_meta_charge,
                    },
                }
            },
            order(state) {
                const baskets = {}
                for (const [key, basket] of Object.entries(state.order.baskets)) {

                    // Get item list
                    const items = basket.items.map(item => {

                        // Deep copy this item, otherwise we'll end up triggering a re-computation of this getter
                        // indefinitely
                        const copy = JSON.parse(JSON.stringify(item))

                        // Get price including all modifiers
                        const modifier_price = item.modifiers
                            .reduce((carry, m) => carry.add(currency(m.price).multiply(m.quantity)), currency(0))

                        copy.price = currency(item.product.selling_price).add(modifier_price).multiply(item.quantity).toString()

                        // Get price including discount from coupon
                        copy.price_after_coupon = copy.price
                        if (basket.coupon) {
                            let discount = basket.coupon.sales_items.find(i => i.uuid == copy.uuid)
                            if (discount) {
                                // Prevent negative price after coupon.
                                let price = currency(copy.price).subtract(currency(discount.discount / 100))
                                if (price.intValue < 0)
                                    price = currency(0)
                                copy.price_after_coupon = price.toString()
                            }
                        }

                        // Get subtitle
                        const modifiers_labels = item.modifiers.map(m => {
                            if (m.quantity == 1)
                                return m.name
                            return `${m.name} (x${m.quantity})`
                        })
                        const text_attribute_labels = item.text_attributes.map(ta => `${ta.name}: ${ta.value}`)
                        copy.subtitle = modifiers_labels.concat(text_attribute_labels).join(', ')

                        return copy
                    })

                    // Get voucher list
                    const vouchers = basket.vouchers.map(item => {
                        const copy = Object.assign(item, {})
                        // Get price including all modifiers
                        copy.price = currency(item.voucher.selling_price).multiply(item.quantity).toString()
                        return copy
                    })

                    // Get applied promotions
                    const promotions_applied = basket[basket.coupon ? 'promotions_after_coupon' : 'promotions_before_coupon']

                    const item_quantities = basket.items
                        .reduce((carry, item) => {
                            carry[item.root_product_id] = (carry[item.root_product_id] || 0) + item.quantity
                            return carry
                        }, {})

                    // Get total
                    const total_price = items.map(item => item.price_after_coupon)
                        .concat(vouchers.map(voucher => voucher.price))
                        .reduce((carry, price) => carry.add(price), currency(0))
                        .subtract(promotions_applied.total_discount)
                        .toString()

                    const total_price_without_coupon = items.map(item => item.price)
                        .concat(vouchers.map(voucher => voucher.price))
                        .reduce((carry, price) => carry.add(price), currency(0))
                        .subtract(promotions_applied.total_discount)
                        .toString()

                    const total_price_without_deductions = items.map(item => item.price)
                        .concat(vouchers.map(voucher => voucher.price))
                        .reduce((carry, price) => carry.add(price), currency(0))
                        .toString()

                    const has_items = items.length > 0 || vouchers.length > 0

                    baskets[key] = {
                        ...basket,
                        items,
                        vouchers,
                        has_items,
                        item_quantities,
                        promotions_applied,
                        total_price,
                        total_price_without_coupon,
                        total_price_without_deductions,
                    }
                }

                return {
                    local_id: state.order.local_id,
                    outlet_slug: state.order.outlet_slug,
                    form: state.order.form,
                    meta_charge: state.order.meta_charge,
                    baskets,
                    tip: state.order.tip,
                    previous_sale_id: state.order.previous_sale_id,
                    checkout_info: state.order.checkout_info,
                    table_cover: state.order.table_cover,
                    menu_option: state.order.menu_option,
                    loading: state.order.loading,
                    checkout_in_progress: state.order.checkout_in_progress,
                    started_at: state.order.started_at,
                }
            },
            displayBaskets(state, getters) {
                return Object.values(getters.order.baskets)
                    .filter(basket => basket.has_items)
                    .sort((a, b) => a.sequence_no - b.sequence_no)
            },
            menuOption(state) {
                return state.order.menu_option
            },
            orderOutletSlug(state) {
                return state.order.outlet_slug
            },
            hasOutlet(state) {
                return state.outlet != null
            },
            outlet(state) {
                return state.outlet
            },
            user(state) {
                return state.user
            },
            location(state) {
                if (state.location == null)
                    return null

                if ((new Date().getTime() - state.location.updated) / 1000 > 180)
                    return null

                return state.location
            },
            seenHintModal(state) {
                return state.seen_hint_modal
            },
            token(state) {
                state.token = storage.getItem('token')
                return state.token
            },
            loginRedirect(state) {
                return state.ui.login_redirect
            },
            embed(state) {
                return state.embed
            },
            app_embed(state) {
                return state.app_embed
            },
            app_mode(state) {
                return state.app_mode
            },
            browse_only(state) {
                return state.app_mode == 'goodies_browse'
            },
            presetFulfilmentType(state) {
                return state.preset_fulfilment_type
            },
            allowPopup(state) {
                return state.allow_popup
            },
            product(state) {
                return (product_id) => {
                    if (state.outlet == null) {
                        return null
                    }
                    for (let outlet of state.outlet.outlets) {
                        for (let category of outlet.menu) {
                            for (let product of category.items) {
                                if (product.product_id == product_id) {
                                    return product
                                }
                                for (let variant of product.variants) {
                                    if (variant.product_id == product_id) {
                                        return variant
                                    }
                                }
                            }
                        }
                    }
                    return null
                }
            },
            productImage(state) {
                return (product_id) => {
                    if (state.outlet == null) {
                        return null
                    }
                    for (let outlet of state.outlet.outlets) {
                        for (let category of outlet.menu) {
                            for (let product of category.items) {
                                if (product.product_id == product_id) {
                                    return product.image
                                }
                                for (let variant of product.variants) {
                                    if (variant.product_id == product_id) {
                                        if (variant.image != null && variant.image.length > 0) {
                                            return variant.image
                                        } else if (product.image != null && product.image.length > 0) {
                                            return product.image
                                        }
                                    }
                                }
                            }
                        }
                    }
                    return null
                }
            }
        },
        mutations: {
            initialiseStore(state) {
                console.log('hydrating store')

                if (storage.getItem('deviceId'))
                    state.device_id = storage.getItem('deviceId')

                if (storage.getItem('user') != null)
                    state.user = JSON.parse(storage.getItem('user'))

                if (storage.getItem('token') != null)
                    state.token = storage.getItem('token')

                let order = storage.getItem('order')
                if (order != null) {
                    order = JSON.parse(order)
                    const stored_data_version = order.data_version || 1
                    const required_data_version = getDefaultOrder().data_version
                    if (stored_data_version == required_data_version) {

                        for (const [outlet_slug, basket] of Object.entries(order.baskets)) {
                            basket.coupon = null
                        }

                        state.order.local_id = order.local_id
                        state.order.outlet_slug = order.outlet_slug
                        state.order.baskets = order.baskets
                        state.order.form = order.form
                        state.order.meta_charge = order.meta_charge
                        state.order.menu_option = order.menu_option
                        state.order.tip = order.tip
                        state.order.checkout_in_progress = order.checkout_in_progress
                    } else {
                        console.info(
                            `Not loading saved order due to different data version - version ${stored_data_version}, required ${required_data_version}`
                        )
                    }
                }

                if (storage.getItem('location') != null)
                    state.location = JSON.parse(storage.getItem('location'))

                if (storage.getItem('loginRedirect'))
                    state.ui.login_redirect = JSON.parse(storage.getItem('loginRedirect'))

                if (storage.getItem('shownHintModal') == '1')
                    state.seen_hint_modal = true

                state.ready = true
                console.log('store ready')
            },
            setOrderLoadedDirectly(state, value) {
                state.order_loaded_directly = value
            },
            setSidebarOpen(state, open = true) {
                state.ui.sidebar_open = open
            },
            setLoading(state, is_loading = true) {
                state.ui.loading = is_loading
            },
            setLoginRedirect(state, route) {
                state.ui.login_redirect = route
            },
            setEmbedClient(state, data) {
                state.embed_client = data
            },
            addVoucher(state, data) {

                // Register order against current outlet
                state.order.outlet_slug = state.outlet.slug

                const basket = getOrCreateOrderBasket(state, data.outlet)
                const vouchers = basket.vouchers

                data.amount = data.voucher.amount
                data.voucher = {
                    product_id: `${data.voucher.amount}_voucher`,
                    product_name: `${state.outlet.currency_symbol + data.voucher.amount} ${state.outlet.voucher_product.product_name}`,
                    selling_price: data.voucher.selling_price,
                }

                // Add sequence number with current timestamp
                data.sequence_no = currentSequenceNo()

                // Add a unique id for line item
                data.uuid = uuidv4()

                let entry = vouchers
                    .find(entry => entry.voucher.product_id == data.voucher.product_id)

                // Add another of existing item
                if (entry) {
                    entry.quantity += data.quantity
                    return
                }

                // Add new item
                vouchers.push(data)
            },
            changeVoucherQuantity(state, data) {

                const basket = getOrCreateOrderBasket(state, data.outlet)
                let vouchers = basket.vouchers

                const entry = vouchers.find(entry => entry.amount == data.amount)

                if (data.action == 'add') {
                    entry.quantity += 1
                } else if (data.action == 'subtract') {
                    entry.quantity -= 1
                } else if (data.action == 'remove') {
                    entry.quantity = 0
                } else {
                    throw new Error(`Unknown action ${data.action}`)
                }

                // Remove items with a non-positive quantity
                vouchers = vouchers.filter(voucher => voucher.quantity > 0)

                Vue.set(state.order.baskets[data.outlet.id], 'vouchers', vouchers)

                // This is kind of a hack since you can't return data from a commit
                if (data.callback)
                    data.callback(entry)
            },
            addProduct(state, data) {

                // Register order against current outlet
                state.order.outlet_slug = state.outlet.slug

                const basket = getOrCreateOrderBasket(state, data.outlet)
                const items = basket.items

                // Build product entry
                const product_entry = {
                    quantity: data.quantity || 1,
                    root_product_id: data.root_product_id,
                    text_attributes: data.text_attributes,
                    // Add sequence number with current timestamp
                    sequence_no: currentSequenceNo(),
                    // Add a unique id for line item
                    uuid: uuidv4(),
                }
                product_entry.product = {
                    product_id: data.product.product_id,
                    product_name: data.product.product_name,
                    selling_price: data.product.selling_price,
                }

                // Add empty text attributes if required
                if (data.hasOwnProperty('text_attributes') == false || !data.text_attributes)
                    product_entry.text_attributes = []

                // Squash modifiers
                product_entry.modifiers = data.modifiers.reduce((carry, modifier) => {
                    if (carry.hasOwnProperty(modifier.id))
                        carry[modifier.id].quantity += modifier.quantity
                    else
                        carry[modifier.id] = modifier
                    return carry
                }, {})
                product_entry.modifiers = Object.values(product_entry.modifiers)

                // Find in basket. Match on product id and combination of modifier id and modifier quantity
                // Don't attempt to match to existing entry if text attributes are present
                const format_modifier = (modifier) => `${modifier.id}-${modifier.quantity}`
                let existing_product_entry = items
                    .find(entry => entry.product.product_id == data.product.product_id &&
                        equals(entry.modifiers.map(m => format_modifier(m)).sort(), data.modifiers.map(m => format_modifier(m)).sort()) &&
                        entry.text_attributes.length == 0)

                // Add another of existing item
                if (existing_product_entry) {
                    existing_product_entry.quantity += data.quantity
                    Vue.set(state.order.baskets[data.outlet.id], 'items', items)

                    this.dispatch('refreshPromotions', {outlet: data.outlet})
                    return
                }

                // Add new item
                items.push(product_entry)

                Vue.set(state.order.baskets[data.outlet.id], 'items', items)

                this.dispatch('refreshPromotions', {outlet: data.outlet})
            },
            removeProducts(state, data) {
                const basket = getOrCreateOrderBasket(state, data.outlet)
                let items = basket.items.filter(e => data.uuids.includes(e.uuid) == false)
                Vue.set(state.order.baskets[data.outlet.id], 'items', items)

                this.dispatch('refreshPromotions', {outlet: data.outlet})
            },
            changeProductQuantity(state, data) {

                const basket = getOrCreateOrderBasket(state, data.outlet)
                let items = basket.items

                let entry = items.find(d => d.uuid == data.uuid)
                if (entry == null) {
                    throw new Error('Basket item not found')
                }

                if (data.action == 'add') {
                    entry.quantity += 1
                } else if (data.action == 'subtract') {
                    entry.quantity -= 1
                } else if (data.action == 'remove') {
                    entry.quantity = 0
                } else {
                    throw new Error(`Unknown action ${data.action}`)
                }

                // Remove items with a non-positive quantity
                items = items.filter(item => item.quantity > 0)

                Vue.set(state.order.baskets[data.outlet.id], 'items', items)

                // This is kind of a hack since you can't return data from a commit
                if (data.callback)
                    data.callback(entry)

                this.dispatch('refreshPromotions', {outlet: data.outlet})
            },
            removeInvalidProducts(state, data) {
                const basket = getOrCreateOrderBasket(state, data.outlet)
                const itemsToRemove = basket.items.filter(item => itemIsValid(item, data.outlet) == false).map(item => item.uuid)

                this.commit('removeProducts', {outlet: data.outlet, uuids: itemsToRemove})
            },
            increaseTip(state) {
                const tip_value = state.order.tip || 0
                if (tip_value >= TIP_MAX)
                    return

                state.order.tip = currency(tip_value).add(TIP_STEP).toString()
            },
            reduceTip(state) {
                const tip_value = state.order.tip || 0

                let new_value = currency(tip_value).subtract(TIP_STEP)
                if (new_value < 0)
                    new_value = 0

                state.order.tip = currency(new_value).toString()
            },
            setTip(state, data) {
                state.order.tip = currency(data).toString()
            },
            setTableCover(state, data) {
                state.order.table_cover = data
            },
            setCoupon(state, data) {
                let coupon = null
                if (data.coupon != null) {
                    coupon = {
                        amount: data.coupon.amount,
                        code: data.coupon.code,
                        description: data.coupon.description,
                        sales_items: data.coupon.sales_items,
                        customer_uses_remaining: data.coupon.customer_uses_remaining,
                    }
                }

                Vue.set(state.order.baskets[data.outlet.id], 'coupon', coupon)

                this.dispatch('refreshPromotions', {outlet: data.outlet})
            },
            setPromotions(state, data) {

                const basket = getOrCreateOrderBasket(state, data.outlet)

                const promotions = {
                    valid_promotions: data.promotions.valid_promotions,
                    total_discount: data.promotions.total_discount,
                    sales_items: data.promotions.sales_items,
                }

                const key = `promotions_${basket.coupon ? 'after' : 'before'}_coupon`

                Vue.set(state.order.baskets[data.outlet.id], key, promotions)
            },
            setMenuOption(state, data) {

                state.order.menu_option = {
                    fulfillment_type: data.fulfillment_type,
                    fulfillment_type_name: data.fulfillment_type_name,
                    display_layout: data.display_layout,
                }
            },
            clearMenuOption(state) {
                state.order = getDefaultOrder()
            },
            clearOrder(state) {
                state.order = getDefaultOrder()
            },
            clearOutlet(state) {
                state.outlet = null
                state.outlet_refreshed_at = moment().unix()
            },
            setOrderForm(state, data) {
                state.order.form = data
            },
            setCheckoutInfo(state, data) {
                state.order.checkout_info = data
            },
            setMetaCharge(state, data) {
                state.order.meta_charge = data
            },
            setReorderSaleId(state, sale_id) {
                state.order.previous_sale_id = sale_id
            },
            setLocation(state, data) {
                state.location = {
                    latitude: data.latitude,
                    longitude: data.longitude,
                    updated: new Date().getTime(),
                }
            },
            setOutlet(state, outlet) {

                outlet.id = outlet.outlet_id
                state.outlet = outlet
                state.outlet_refreshed_at = moment().unix()
            },
            setEmbed(state, embed) {
                state.embed = embed
            },
            setAppEmbed(state, data) {
                state.app_embed = data
            },
            setAppMode(state, data) {
                state.app_mode = data == null ? null : data.toLowerCase()
            },
            setPresetFulfilmentType(state, data) {
                state.preset_fulfilment_type = data
            },
            shownHintModal(state, data=true) {
                state.seen_hint_modal = data
            },
            refreshToken(state, data) {
                state.token = data.token
                state.user = data.user
                state.token_checked = true
            },
            login(state, data) {
                state.user = data.user
                state.token = data.token
                state.token_checked = true
            },
            logout(state) {
                state.user = null
                state.token = null
                state.token_checked = false
                
                // Left as a Vuex mutation as this request is not actioned
                app.http.post('/jwt/invalidate')
                    .catch(error => {
                        console.error('Token invalidation error:', error)
                    })
            },
            setToken(state, data) {
                state.token = data
            },
            setAllowPopup(state, data = true) {
                state.allow_popup = data
            },
            setCheckoutInProgress(state, data) {
                state.order.checkout_in_progress = data
            },
        },
        actions: {

            refreshOutlet(context, data) {

                const force_refresh = data.force_refresh == true
                if (context.state.outlet_refreshed_at >= moment().subtract(1, 'hours').unix() && force_refresh == false)
                    return

                const request_data = {}
                if (context.state.order != null && context.state.order.menu_option) {
                    request_data.fulfillment_type = context.state.order.menu_option.fulfillment_type
                    request_data.display_layout_id = context.state.order.menu_option.display_layout.id
                }

                return app.http.get('outlet/' + context.state.outlet.slug, request_data)
                    .then(response => {
                        context.commit('setOutlet', response.data.outlet)
                    })
                    .catch(error => {
                        const outlet_slug = context.state.outlet.slug
                        app.logException(new Error('Error refreshing outlets'), {
                            outlet_slug,
                            error
                        })
                        reject(error)
                    })
            },
            refreshPromotions(context, data) {

                const outlet = data.outlet

                if (context.state.order == null || context.state.order.menu_option == null) {
                    return
                }

                if (context.state.outlet == null || context.state.hasItems == false) {
                    return
                }

                const basket = context.getters.order.baskets[data.outlet.id]
                const price_key = basket.coupon ? 'price_after_coupon' : 'price'

                const items = basket.items.map(item => {
                    return {
                        uuid: item.uuid,
                        product_id: item.product.product_id,
                        quantity: item.quantity,
                        name: item.product.product_name,
                        price: currency(item[price_key]).intValue,
                    }
                })

                const request_data = {
                    outlet_slug: data.outlet.slug,
                    basket: items,
                    type: context.state.order.menu_option.fulfillment_type,
                    display_layout_id: context.state.order.menu_option.display_layout.id,
                }

                context.state.order.loading = true

                return new Promise((resolve, reject) => {
                    app.http.post(`/outlet/${context.state.outlet.slug}/promotions`, request_data)
                        .then(response => {
                            context.state.order.loading = false

                            context.commit('setPromotions', {
                                promotions: {
                                    valid_promotions: response.data.valid_promotions,
                                    total_discount: currency(response.data.total_discount / 100).toString(),
                                    sales_items: response.data.sales_items,
                                },
                                outlet,
                            })
                            resolve(response)
                        })
                        .catch(error => {

                            context.state.order.loading = false

                            // Log error, not much we can do at this point
                            app.logException(new Error('Error fetching promotions_before_coupon'), {
                                data,
                                error
                            })

                            reject(error)
                        })
                })
            },
            refreshState(context, data) {

                const force_refresh = data.force_refresh == true
                let promises = []

                // Refresh outlet
                promises.push(this.dispatch('refreshOutlet', {force_refresh}))

                // Add refresh promotion jobs
                promises = promises.concat(Object.values(context.state.order.baskets)
                    .map(basket => this.dispatch('refreshPromotions', {outlet: basket.outlet})))

                return Promise.all(promises)
            },
        }
    })

    store.subscribeAction((action, state) => {
        console.info(`vuex action executed ${action.type}`, action.payload)
    })

    store.subscribe((mutation, state) => {

        console.info(`vuex mutation executed ${mutation.type}`)

        const order_update_mutations = [
            'addProduct',
            'changeProductQuantity',
            'setOrderForm',
            'setMetaCharge',
            'setCoupon',
            'addVoucher',
            'changeVoucherQuantity',
            'setMenuOption',
            'clearMenuOption',
            'setCheckoutInProgress',
            'removeProducts',
        ]

        if (mutation.type == 'setToken')
            storage.setItem('token', mutation.payload)

        if (['login', 'refreshToken'].includes(mutation.type)) {
            storage.setItem('user', JSON.stringify(state.user))
            storage.setItem('token', mutation.payload.token)
        }

        if (order_update_mutations.includes(mutation.type)) {
            // We may experience the quota exceeded exception in some browsers, even when storage is available
            try {
                storage.setItem('order', JSON.stringify(state.order))
            } catch (error) {
                // Prevent an invalid state from being left behind once the order has been completed
                storage.removeItem('order')
                app.logException(new Error('Error setting local storage with order data'), {
                    error
                })
            }
        }

        if (mutation.type == 'clearOrder')
            storage.removeItem('order')

        if (mutation.type == 'setLocation')
            storage.setItem('location', JSON.stringify(state.location))

        if (mutation.type == 'setLoginRedirect')
            storage.setItem('loginRedirect', JSON.stringify(mutation.payload))

        if (mutation.type == 'logout')
            ['user', 'token'].forEach(key => storage.removeItem(key))

        if (mutation.type == 'shownHintModal')
            storage.setItem('shownHintModal', mutation.payload ? '1' : '0')
    })

    return store
}

export default getStore



// WEBPACK FOOTER //
// ./src/js/store.js